import apiClient from './api';
import { hashtagApiPaths } from '@/constants/hashtagApiPaths';
import { buildHashtagCampaignCreatePayload } from '@/constants/hashtagPlatforms';
import type {
  HashtagCampaign,
  HashtagCampaignCatalogItem,
  HashtagCampaignCreate,
  HashtagCampaignScrapeResponse,
  HashtagCampaignSettingsPatch,
  HashtagRead,
  HashtagScrapeRequest,
} from '@/types/hashtag.types';
import { findCampaignBySearchTerm } from '@/utils/hashtagCampaignMatch';
import {
  groupHashtagReadsToCampaigns,
  normalizeHashtagCampaignList,
} from '@/utils/hashtagCampaignMapper';
import {
  HASHTAG_CAMPAIGN_CREATE_TIMEOUT_MS,
  isRecoverableHashtagCreateError,
} from '@/utils/hashtagCampaignRegister';

const CAMPAIGN_CREATED = 201;
const SCRAPE_ACCEPTED = 202;

export interface RegisterHashtagPayload {
  name: string;
  platform_codes?: string[];
}

export interface RegisterHashtagResult {
  campaign: HashtagCampaignCatalogItem;
  /** Create failed with timeout/502 but campaign exists on GET list. */
  recoveredAfterCreateError: boolean;
}

function buildCreateBody(payload: RegisterHashtagPayload): HashtagCampaignCreate {
  return buildHashtagCampaignCreatePayload(payload.name, payload.platform_codes);
}

function campaignFromRegisterResponse(
  rows: HashtagRead[],
  requestedName: string
): HashtagCampaignCatalogItem | undefined {
  const campaigns = groupHashtagReadsToCampaigns(rows);
  if (campaigns.length === 1) {
    return { ...campaigns[0], name: campaigns[0].name || requestedName };
  }
  return findCampaignBySearchTerm(campaigns, requestedName) ?? campaigns[0];
}

async function resolveCampaignAfterCreate(
  body: HashtagCampaignCreate,
  responseData: HashtagRead[] | HashtagCampaign
): Promise<HashtagCampaignCatalogItem> {
  try {
    const campaigns = await hashtagCampaignService.list();
    const fromList = findCampaignBySearchTerm(campaigns, body.name);
    if (fromList) return fromList;
  } catch {
    /* fallback to 201 body */
  }

  const data = responseData;
  if (data && typeof data === 'object' && 'hashtag_campaign_code' in data && 'platforms' in data) {
    return normalizeHashtagCampaignList([data as HashtagCampaign])[0];
  }

  const fromRows = campaignFromRegisterResponse(Array.isArray(data) ? data : [], body.name);
  if (fromRows) return fromRows;

  throw new Error('Invalid response when registering hashtag: could not resolve campaign.');
}

export const hashtagCampaignService = {
  /** GET /api/tenant/hashtags → 200, campañas agrupadas (ADMIN tenant-scoped) */
  list: async (): Promise<HashtagCampaignCatalogItem[]> => {
    const response = await apiClient.get<HashtagCampaign[]>(hashtagApiPaths.list);
    return normalizeHashtagCampaignList(response.data);
  },

  findCampaignByName: async (name: string): Promise<HashtagCampaignCatalogItem | null> => {
    const campaigns = await hashtagCampaignService.list();
    return findCampaignBySearchTerm(campaigns, name) ?? null;
  },

  /**
   * POST /api/tenant/hashtags/campaigns → 201
   * On 502/504/timeout, recovers via GET list when the campaign was created anyway.
   */
  register: async (payload: RegisterHashtagPayload): Promise<RegisterHashtagResult> => {
    const body = buildCreateBody(payload);

    try {
      const response = await apiClient.post<HashtagRead[] | HashtagCampaign>(
        hashtagApiPaths.create,
        body,
        {
          timeout: HASHTAG_CAMPAIGN_CREATE_TIMEOUT_MS,
          validateStatus: status => status === CAMPAIGN_CREATED,
        }
      );

      if (response.status !== CAMPAIGN_CREATED) {
        throw new Error(
          `Unexpected status ${response.status} from ${hashtagApiPaths.create}; expected ${CAMPAIGN_CREATED}.`
        );
      }

      const campaign = await resolveCampaignAfterCreate(body, response.data);
      return { campaign, recoveredAfterCreateError: false };
    } catch (err: unknown) {
      if (!isRecoverableHashtagCreateError(err)) {
        throw err;
      }

      const recovered = await hashtagCampaignService.findCampaignByName(body.name);
      if (recovered) {
        return { campaign: recovered, recoveredAfterCreateError: true };
      }

      throw err;
    }
  },

  patchCampaignSettings: async (
    campaignCode: string,
    patch: HashtagCampaignSettingsPatch
  ): Promise<HashtagCampaign> => {
    const response = await apiClient.patch<HashtagCampaign>(
      hashtagApiPaths.campaignSettings(campaignCode),
      patch
    );
    return response.data;
  },

  scrapeCampaign: async (
    campaignCode: string,
    body: HashtagScrapeRequest = {}
  ): Promise<HashtagCampaignScrapeResponse> => {
    const response = await apiClient.post<HashtagCampaignScrapeResponse>(
      hashtagApiPaths.scrapeCampaign(campaignCode),
      body,
      { validateStatus: status => status === SCRAPE_ACCEPTED }
    );

    if (response.status !== SCRAPE_ACCEPTED) {
      throw new Error(
        `Unexpected status ${response.status} from campaign scrape; expected ${SCRAPE_ACCEPTED}.`
      );
    }

    return response.data;
  },
};
